import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@zagru/design-system';
import { useAuth, useT } from '@zagru/platform';
import { consumePendingReturnTo, safeReturnTo } from '@/app/state/appStores';
import { ShellSkeleton } from '@/app/shell/ShellSkeleton';
import { AuthFrame } from './AuthFrame';
import { signInFailureCopy } from './authCopy';
import { autoSignInRetry } from './autoSignIn';

/**
 * `/auth/callback`: the AuthProvider completes the code exchange from the URL; this page holds the shell's
 * silhouette meanwhile and continues to the captured `returnTo`. When the identity provider had no session for a
 * silent attempt, the user continues to sign-in with the destination preserved. A failed exchange goes back to the
 * sign-in form once by itself (`autoSignIn.ts`); a second failure is explained here.
 */
export function CallbackPage() {
  const t = useT();
  const navigate = useNavigate();
  const { status, error } = useAuth();

  useEffect(() => {
    if (status === 'authenticated') {
      autoSignInRetry.clear();
      navigate(safeReturnTo(consumePendingReturnTo(), '/'), { replace: true });
    } else if (status === 'anonymous') {
      const returnTo = consumePendingReturnTo();
      const target = returnTo ? `/login?returnTo=${encodeURIComponent(safeReturnTo(returnTo, '/'))}` : '/login';
      navigate(target, { replace: true });
    } else if (status === 'error' && autoSignInRetry.allowed()) {
      navigate('/login', { replace: true });
    }
  }, [status, navigate]);

  if (status === 'error' && !autoSignInRetry.allowed()) {
    const copy = signInFailureCopy(error, t);
    return (
      <AuthFrame
        title={copy.title}
        description={copy.description}
        technical={error ?? undefined}
        actions={
          <Button variant="primary" size="lg" fullWidth 
            onClick={() => {
              autoSignInRetry.clear();
              navigate('/login', { replace: true });
            }}
          >
            {t('auth.startAgain')}
          </Button>
        }
      />
    );
  }

  return <ShellSkeleton label={t('auth.signingYouIn')} />;
}
